import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { showToast } from '../../../helpers';
import {
  getCalendarSyncStatus,
  planCalendarSync,
  runCalendarSync,
  TCalendarSyncPlan,
  TCalendarSyncRun,
} from '../../../requests/opsCalendarSync';
import {
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  PageBody,
  PageHeader,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
} from '../ui';

const when = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="px-5 py-3">
    <div className="text-xs uppercase tracking-wider text-gray-two">
      {label}
    </div>
    <div className="mt-0.5 text-lg font-semibold text-gray-five">{value}</div>
  </div>
);

const RunLine: React.FC<{ r: TCalendarSyncRun }> = ({ r }) => (
  <tr>
    <td className={tdClass}>{when(r.at)}</td>
    <td className={tdClass}>
      <Pill value={r.mode} tone={r.mode === 'live' ? 'good' : 'info'} />
    </td>
    <td className={tdClass}>
      {r.outbound
        ? `${r.outbound.create} new · ${r.outbound.update} changed · ${r.outbound.remove} removed`
        : '—'}
      {r.outbound && !r.outbound.applied && (
        <span className="ml-1 text-xs text-gray-two">(not applied)</span>
      )}
    </td>
    <td className={tdClass}>
      {r.inbound
        ? `${r.inbound.insert} new · ${r.inbound.update} changed · ${r.inbound.remove} cleared · ${r.inbound.users} calendars read`
        : '—'}
      {r.teachingDropped ? (
        <span className="ml-1 text-xs text-gray-two">
          ({r.teachingDropped} were their own classes)
        </span>
      ) : null}
    </td>
    <td className={tdClass}>
      {r.error ? (
        <span className="text-red-700">{r.error}</span>
      ) : r.userErrors?.length ? (
        <span className="text-yellow-900">
          {r.userErrors.length} calendar{r.userErrors.length === 1 ? '' : 's'}{' '}
          failed
        </span>
      ) : r.ok ? (
        <Pill value="ok" tone="good" />
      ) : (
        '—'
      )}
    </td>
  </tr>
);

/**
 * Outlook sync — a class with an instructor becomes an event in their
 * Outlook; their busy time becomes away blocks here. `Preview` reads Outlook
 * and shows both plans without writing; `Sync now` applies them in live mode.
 */
const OpsCalendarSync: React.FC = () => {
  const qc = useQueryClient();
  const [preview, setPreview] = useState<TCalendarSyncPlan | null>(null);
  const status = useQuery({
    queryKey: ['opsCalendarSync', 'status'],
    queryFn: getCalendarSyncStatus,
  });
  const plan = useMutation({
    mutationFn: planCalendarSync,
    onSuccess: (p) => {
      setPreview(p);
      if (!p.ran) showToast(p.reason ?? 'Could not plan', 'error');
    },
    onError: () => showToast('Preview failed', 'error'),
  });
  const run = useMutation({
    mutationFn: runCalendarSync,
    onSuccess: (p) => {
      setPreview(p);
      qc.invalidateQueries({ queryKey: ['opsCalendarSync'] });
      showToast(
        p.ran ? 'Sync finished' : p.reason ?? 'Sync did not run',
        p.ran ? 'success' : 'error',
      );
    },
    onError: () => showToast('Sync failed', 'error'),
  });

  if (status.isLoading || !status.data) return <LoadingSpinner />;
  const s = status.data;
  const busy = plan.isPending || run.isPending;

  return (
    <>
      <PageHeader
        title="Outlook sync"
        meta={
          <span className="text-sm text-gray-bold">
            Classes you assign appear in the instructor&apos;s Outlook; their
            own busy time shows here as away blocks. The portal never reads what
            an event is, only when it is.
          </span>
        }
        actions={
          <div className="flex gap-2">
            <button
              className={btnSecondary}
              disabled={busy || !s.configured}
              onClick={() => plan.mutate()}
            >
              Preview
            </button>
            <button
              className={btnPrimary}
              disabled={busy || !s.configured || s.mode !== 'live'}
              title={
                s.mode !== 'live'
                  ? 'CALENDAR_SYNC is not live — preview only'
                  : undefined
              }
              onClick={() => run.mutate()}
            >
              Sync now
            </button>
          </div>
        }
      />
      <PageBody>
        <Card>
          <div className="grid grid-cols-2 divide-x divide-white-three md:grid-cols-5">
            <Stat
              label="Mode"
              value={
                <Pill
                  value={s.mode}
                  tone={
                    s.mode === 'live'
                      ? 'good'
                      : s.mode === 'record'
                      ? 'info'
                      : 'neutral'
                  }
                />
              }
            />
            <Stat
              label="Credential"
              value={
                s.configured ? (
                  <Pill value="set" tone="good" />
                ) : (
                  <span className="text-sm text-red-700">
                    missing {s.missing.join(', ')}
                  </span>
                )
              }
            />
            <Stat
              label="Scope"
              value={s.scope === 'all' ? 'every class' : 'portal-born'}
            />
            <Stat label="Events placed" value={s.counts.events} />
            <Stat label="Outlook blocks" value={s.counts.blocks} />
          </div>
          <div className="border-t border-white-three px-5 py-2 text-xs text-gray-bold">
            Looks {s.horizonDays} days ahead · {s.counts.instructorsWithEmail}{' '}
            of {s.counts.instructors} active instructors have an email
            {s.allowlist.length > 0 && (
              <> · limited to {s.allowlist.join(', ')}</>
            )}{' '}
            · Outlook &quot;{s.blockShowAs.join('” and “')}&quot; count as away
          </div>
        </Card>

        {s.instructorsWithoutEmail.length > 0 && (
          <Card
            title="Not synced — no email on the instructor"
            className="mt-5"
          >
            <div className="px-5 py-3 text-sm text-gray-bold">
              {s.instructorsWithoutEmail.map((i) => i.displayName).join(', ')}.{' '}
              <Link className="text-blue-primary" to={PATH.opsInstructors}>
                Add their address on Instructors
              </Link>{' '}
              and the next run includes them.
            </div>
          </Card>
        )}

        {s.instructorsOutsideTenant.length > 0 && (
          <Card
            title="Outside the Learn It tenant — invited by ICS, not synced"
            className="mt-5"
          >
            <div className="px-5 py-3 text-sm text-gray-bold">
              Outlook can only be reached for {s.tenantDomains.join(', ')}{' '}
              mailboxes. These instructors are on their own address, so a class
              reaches them as a calendar invite and their busy time is not read:{' '}
              {s.instructorsOutsideTenant.map((i) => i.displayName).join(', ')}.
            </div>
          </Card>
        )}

        {s.lastRun?.unmapped && s.lastRun.unmapped.length > 0 && (
          <Card
            title="Classes whose instructor name matched nobody"
            className="mt-5"
          >
            <div className="px-5 py-3 text-sm text-gray-bold">
              {s.lastRun.unmapped
                .map((u) => `${u.name} (${u.classes})`)
                .join(' · ')}
              . Add the spelling as a variant on Instructors.
            </div>
          </Card>
        )}

        {preview?.plan && (
          <Card
            title={
              preview.run?.mode === 'dry-run'
                ? 'Preview — nothing was written'
                : 'This run'
            }
            className="mt-5"
            right={
              <button className={btnSecondary} onClick={() => setPreview(null)}>
                Close
              </button>
            }
          >
            <div className="grid gap-0 md:grid-cols-2 md:divide-x md:divide-white-three">
              <div className="px-5 py-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-two">
                  Into Outlook
                </div>
                {[
                  ['Create', preview.plan.events.create],
                  ['Update', preview.plan.events.update],
                ].map(([label, rows]) => (
                  <div key={label as string} className="mb-2 text-sm">
                    <span className="font-medium text-gray-four">
                      {label as string} ({(rows as unknown[]).length})
                    </span>
                    <ul className="ml-4 list-disc text-gray-bold">
                      {(
                        rows as {
                          subject: string;
                          instructor: string;
                          start: string;
                        }[]
                      )
                        .slice(0, 25)
                        .map((e, i) => (
                          <li key={i}>
                            {e.subject} · {e.instructor} · {when(e.start + 'Z')}
                          </li>
                        ))}
                    </ul>
                  </div>
                ))}
                <div className="text-sm">
                  <span className="font-medium text-gray-four">
                    Remove ({preview.plan.events.remove.length})
                  </span>
                  <ul className="ml-4 list-disc text-gray-bold">
                    {preview.plan.events.remove.slice(0, 25).map((e, i) => (
                      <li key={i}>
                        {e.instructor} · class {e.classId}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="px-5 py-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-two">
                  From Outlook (away blocks)
                </div>
                <div className="text-sm text-gray-bold">
                  {preview.plan.blocks.insert.length} new ·{' '}
                  {preview.plan.blocks.update.length} changed ·{' '}
                  {preview.plan.blocks.remove.length} cleared
                </div>
                <ul className="ml-4 mt-1 list-disc text-sm text-gray-bold">
                  {preview.plan.blocks.insert.slice(0, 25).map((b, i) => (
                    <li key={i}>
                      {when(b.from)} – {when(b.to)} · {b.note}
                    </li>
                  ))}
                </ul>
                {preview.run?.userErrors &&
                  preview.run.userErrors.length > 0 && (
                    <div className="mt-3 text-sm text-yellow-900">
                      Could not read:{' '}
                      {preview.run.userErrors
                        .map((u) => `${u.email} (${u.error.slice(0, 80)})`)
                        .join('; ')}
                    </div>
                  )}
              </div>
            </div>
          </Card>
        )}

        <Card title="Runs" className="mt-5">
          {s.runs.length === 0 ? (
            <EmptyState
              title="No runs yet"
              hint={
                s.configured
                  ? 'Runs every 15 minutes in record or live mode, or press Preview.'
                  : 'Set GRAPH_CAL_TENANT_ID, GRAPH_CAL_CLIENT_ID and GRAPH_CAL_CLIENT_SECRET on the box.'
              }
            />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>When</th>
                  <th className={thClass}>Mode</th>
                  <th className={thClass}>Into Outlook</th>
                  <th className={thClass}>From Outlook</th>
                  <th className={thClass}>Result</th>
                </tr>
              </thead>
              <tbody>
                {s.runs.map((r, i) => (
                  <RunLine key={i} r={r} />
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </PageBody>
    </>
  );
};

export default OpsCalendarSync;
